import { useRef } from "react";

import { Dropdown, ItemIcon, Type } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import getIsActivationLimitReached from "app/pages/activation/helpers/getIsActivationLimitReached";
import { activationSetSearch } from "app/pages/activation/helpers/home/header";
import {
    ACTIVATION_PROJECT_ROUTE,
    DEFAULT_ACTIVATION_GRANULARITY,
    DEFAULT_ACTIVATION_WEB_SOURCE,
} from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { ActivationComponentAssets } from "../../helpers/ActivationAssets";
import { FlatDropdownButton } from "../Shared/FlatDropdownButton/FlatDropdownButton";

import { ActivationItem } from "./components/ActivationItem/ActivationItem";
import { Footer } from "./components/Footer/Footer";
import { BUTTON_WIDTH, DROPDOWN_SCROLL_HEIGHT_MAX, DROPDOWN_SCROLL_MIN, DROPDOWN_SCROLL_WIDTH } from "./constants";
import { activationSetDDKeys } from "./data-automation";
import * as SC from "./styles";
import { NO_RESULT, PLACEHOLDER } from "./text";
import type { ActivationSetDropdownSchema } from "./type";

export const ActivationSetDropdown = ({
    isMultiple,
    defaultIcon,
    favicons,
    activationSetList,
    isLoading,
    activationId,
    onOpen,
    setDefaultPage,
    defaultPageId,
}: ActivationSetDropdownSchema) => {
    const track = activationTracking.home;

    const selectedSetName = activationSetList.find((item) => {
        return item.id === activationId;
    })?.name;

    const activationDDRef = useRef(null);

    const maximum = ActivationComponentAssets.activationSetsLimitation;

    const isMaxReached = getIsActivationLimitReached(activationSetList.length);

    const onClick = () => {
        track.trackCreateNewActivationFromDD();
        activationDDRef?.current?.closePopup();
        onOpen();
    };

    const onDefaultPageSelected = (selectedActivationId: string, name: string, countryId: number) => {
        activationDDRef?.current?.closePopup();
        setDefaultPage({ activationId: selectedActivationId, name, country: countryId, withRedirect: true });
    };

    const footerWrapped = () =>
        isLoading ? null : (
            <Footer
                maximum={maximum}
                currentSetsNumber={activationSetList.length}
                isMaxReached={isMaxReached}
                onClick={onClick}
            />
        );

    const noResultWrapped = () => <SC.NoResult>{translate(NO_RESULT)}</SC.NoResult>;

    const onItemClick = async ({ id, countryId, currentId }) => {
        if (id !== currentId) {
            track.trackSelectActivationFromDD();
            await proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
                key: id,
                country: countryId,
                webSource: DEFAULT_ACTIVATION_WEB_SOURCE,
                timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
            });
        }
    };

    const activationDropdown = (
        <Dropdown
            ref={activationDDRef}
            appendTo=".activation-set-dd-wrapper"
            shouldScrollToSelected
            buttonWidth={BUTTON_WIDTH}
            width={DROPDOWN_SCROLL_WIDTH}
            height={DROPDOWN_SCROLL_HEIGHT_MAX}
            dropdownPopupMinScrollHeight={DROPDOWN_SCROLL_MIN}
            scrollAreaMinHeight={DROPDOWN_SCROLL_MIN}
            dropdownPopupHeight={DROPDOWN_SCROLL_HEIGHT_MAX}
            searchPlaceHolder={translate(PLACEHOLDER)}
            dropdownPopupPlacement="bottom-left"
            hasSearch
            closeOnItemClick={false}
            // @ts-expect-error Wrong type
            searchFilter={activationSetSearch}
            footerComponent={footerWrapped}
            getNoResultsComponent={noResultWrapped}
            onClick={onItemClick}
            stopScrollPropagation
        >
            {[
                <FlatDropdownButton
                    isLoading={isLoading}
                    icon={defaultIcon}
                    text={selectedSetName}
                    key="activation-dd-button"
                />,
                ...activationSetList.map(
                    ({ name, id, country, sharedBy, primaryDomain, competitorsDomains, isOwn }) => (
                        <ActivationItem
                            currentId={activationId}
                            id={id}
                            favicons={favicons}
                            key={id}
                            name={name}
                            competitors={competitorsDomains}
                            countryId={country}
                            isSelected={id === activationId}
                            primaryDomain={primaryDomain}
                            sharedBy={sharedBy}
                            onDefaultPageSelected={onDefaultPageSelected}
                            isDefaultPageItem={defaultPageId === id}
                            isOwn={isOwn}
                        />
                    ),
                ),
            ]}
        </Dropdown>
    );

    const singleActivationName = activationSetList?.[0]?.name;

    return (
        <SC.TitleWrapper
            data-automation={activationSetDDKeys.title}
            className="activation-set-dd-wrapper"
        >
            {isMultiple ? (
                <> {activationDropdown}</>
            ) : (
                <>
                    <SC.IconWrapper data-automation={activationSetDDKeys.icon}>
                        <ItemIcon
                            iconType={Type.Website}
                            iconSrc={defaultIcon}
                        />
                    </SC.IconWrapper>
                    <SC.DomainTitle>{singleActivationName}</SC.DomainTitle>
                </>
            )}
        </SC.TitleWrapper>
    );
};

ActivationSetDropdown.displayName = "ActivationSetDropdown";
